import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Group, Modal, MultiSelect, Radio, Stack, Text, TextInput } from "@mantine/core";
import { toast } from "sonner";
import { meetingRoomsApi, tenantApi, type IssuedTenantCalendarFeed } from "@/lib/api/client";
import { buildIssueBody, canIssue, describeFeed, type FeedContents, type FeedLayout } from "@/lib/calendar-feeds";

const LAYOUTS: { value: FeedLayout; label: string; description: string }[] = [
  { value: "all", label: "All rooms, one calendar", description: "Every meeting room together in one calendar." },
  { value: "selected", label: "Selected rooms, one calendar", description: "The rooms you pick, together in one calendar." },
  {
    value: "per_room",
    label: "One calendar per room",
    description: "A separate calendar and URL for each room you pick, so you can show or hide rooms one by one.",
  },
];

export function CalendarFeedsCard() {
  const queryClient = useQueryClient();
  const [issuing, setIssuing] = useState(false);
  const [newName, setNewName] = useState("");
  const [layout, setLayout] = useState<FeedLayout>("all");
  const [roomIds, setRoomIds] = useState<string[]>([]);
  const [contents, setContents] = useState<FeedContents>("own");
  const [issued, setIssued] = useState<IssuedTenantCalendarFeed[]>([]);
  const [confirmRevoke, setConfirmRevoke] = useState<{ id: number; name: string } | null>(null);

  const { data: feeds = [], isLoading } = useQuery({
    queryKey: ["tenant-calendar-feeds"],
    queryFn: () => tenantApi.listCalendarFeeds().then((r) => r.data),
  });

  const { data: rooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ["meeting-rooms", "for-calendar-feed"],
    queryFn: () => meetingRoomsApi.getRooms().then((r) => r.data),
    enabled: issuing && layout !== "all",
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["tenant-calendar-feeds"] });

  const resetForm = () => {
    setNewName("");
    setLayout("all");
    setRoomIds([]);
    setContents("own");
  };

  const issueMutation = useMutation({
    mutationFn: () =>
      tenantApi.issueCalendarFeed(
        buildIssueBody({ name: newName, layout, roomIds: roomIds.map(Number), contents }),
      ),
    onSuccess: (res) => {
      invalidate();
      // Tolerate a stale backend from mid-deploy: the pre-rooms endpoint
      // returned the single feed directly, with no `feeds` wrapper.
      setIssued(res.data.feeds ?? [res.data as IssuedTenantCalendarFeed]);
      setIssuing(false);
      resetForm();
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || "Failed to issue feed");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: number) => tenantApi.revokeCalendarFeed(id),
    onSuccess: () => {
      invalidate();
      setConfirmRevoke(null);
      toast.success("Calendar feed revoked");
    },
    onError: () => toast.error("Failed to revoke feed"),
  });

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url).then(
      () => toast.success("Subscribe URL copied"),
      () => toast.error("Couldn't copy — select the URL and copy manually"),
    );
  };

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text fw={500}>Meeting-room calendar feed</Text>
            <Text fz="sm" c="dimmed" maw={520}>
              Subscribe in Google / Apple / Outlook calendar to see meeting-room bookings. Treat the URL like a
              password — anyone with the link can read the feed. Revoke it if it leaks.
            </Text>
          </Stack>
          <Button size="xs" onClick={() => setIssuing(true)} disabled={issuing}>
            Subscribe
          </Button>
        </Group>

        {isLoading ? (
          <Text c="dimmed" fz="sm">Loading…</Text>
        ) : feeds.length === 0 ? (
          <Text c="dimmed" fz="sm">
            No feeds yet. Subscribe to get a URL.
          </Text>
        ) : (
          <Stack gap="xs">
            {feeds.map((feed) => (
              <Card key={feed.id} withBorder padding="sm" style={{ opacity: feed.is_active ? 1 : 0.55 }}>
                <Group justify="space-between" wrap="nowrap">
                  <Stack gap={2} miw={0} flex={1}>
                    <Group gap="xs">
                      <Text fw={500} truncate>{feed.name || "Untitled feed"}</Text>
                      {!feed.is_active && (
                        <Text fz="xs" c="red">{feed.revoked_at ? "Revoked" : "Expired"}</Text>
                      )}
                    </Group>
                    <Text fz="xs" c="dimmed">{describeFeed(feed)}</Text>
                    <Text fz="xs" c="dimmed">
                      Created {new Date(feed.created_at).toLocaleDateString()}
                      {feed.created_by ? ` by ${feed.created_by}` : ""}
                      {feed.last_used_at
                        ? ` · Last fetched ${new Date(feed.last_used_at).toLocaleString()}${
                            feed.can_view_accesses && feed.last_used_remote_addr ? ` from ${feed.last_used_remote_addr}` : ""
                          }`
                        : " · Never fetched"}
                    </Text>
                  </Stack>
                  {feed.is_active && feed.can_manage && (
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => setConfirmRevoke({ id: feed.id, name: feed.name || "Untitled feed" })}
                    >
                      Revoke
                    </Button>
                  )}
                </Group>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>

      <Modal opened={issuing} onClose={() => setIssuing(false)} title="Subscribe to meeting rooms">
        <Stack gap="md">
          <TextInput
            label="Name"
            description="So you can recognise this feed later."
            placeholder="Team calendar"
            value={newName}
            onChange={(e) => setNewName(e.currentTarget.value)}
          />
          <Radio.Group label="Rooms" value={layout} onChange={(v) => setLayout(v as FeedLayout)}>
            <Stack gap="xs" mt="xs">
              {LAYOUTS.map((o) => (
                <Radio key={o.value} value={o.value} label={o.label} description={o.description} />
              ))}
            </Stack>
          </Radio.Group>
          {layout !== "all" && (
            <MultiSelect
              label="Pick rooms"
              data={rooms.map((r) => ({ value: String(r.id), label: r.description || r.name }))}
              value={roomIds}
              onChange={setRoomIds}
              searchable
              disabled={roomsLoading}
              placeholder={roomsLoading ? "Loading rooms…" : "Choose one or more rooms"}
              nothingFoundMessage="No rooms"
            />
          )}
          <Radio.Group label="Show" value={contents} onChange={(v) => setContents(v as FeedContents)}>
            <Stack gap="xs" mt="xs">
              <Radio value="own" label="Only our bookings" />
              <Radio value="all" label="All bookings" description="Other companies' bookings show as busy, without details." />
            </Stack>
          </Radio.Group>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setIssuing(false)}>Cancel</Button>
            <Button
              onClick={() => issueMutation.mutate()}
              loading={issueMutation.isPending}
              disabled={!canIssue(layout, roomIds.map(Number))}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={issued.length > 0}
        onClose={() => setIssued([])}
        title={issued.length > 1 ? "Your subscribe URLs" : "Your subscribe URL"}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Stack gap="md">
          <Text fz="sm" c="dimmed">
            Copy {issued.length > 1 ? "each URL" : "this URL"} now — it is only shown once. Paste it into Google /
            Apple / Outlook's "Add by URL" / "Subscribe to calendar" option.
          </Text>
          {issued.map((feed) => (
            <Stack key={feed.token.id} gap={4}>
              <Text fz="xs" fw={500}>{feed.token.name || "Untitled feed"}</Text>
              <Card withBorder padding="xs" style={{ wordBreak: "break-all" }}>
                <Text fz="xs" ff="monospace">{feed.subscribe_url}</Text>
              </Card>
              <Group justify="flex-end">
                <Button size="xs" variant="outline" onClick={() => handleCopy(feed.subscribe_url)}>
                  Copy URL
                </Button>
              </Group>
            </Stack>
          ))}
          <Group justify="flex-end">
            <Button onClick={() => setIssued([])}>Done</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={!!confirmRevoke} onClose={() => setConfirmRevoke(null)} title="Revoke calendar feed?">
        <Stack gap="md">
          <Text fz="sm" c="dimmed">
            Calendar apps subscribed to <strong>{confirmRevoke?.name}</strong> will stop receiving updates.
            This can't be undone — create a new feed if you want to re-subscribe.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setConfirmRevoke(null)}>Cancel</Button>
            <Button
              color="red"
              loading={revokeMutation.isPending}
              onClick={() => confirmRevoke && revokeMutation.mutate(confirmRevoke.id)}
            >
              Revoke
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
